import React from "react";
import TravelFormInput from "src/components/travel/form/TravelFormInput";
import TravelFormSelect from "src/components/travel/form/TravelFormSelect";

const LOCALE = "en_GB";

const getLabel = (item) => item?.labels?.[LOCALE] || item?.defaultLabel || item?.name || "";

const getGroups = (data) => (Array.isArray(data) ? data : data?.groups || []);

const byOrder = (a, b) => {
  const diff = (a?.sequence ?? 0) - (b?.sequence ?? 0);
  if (diff !== 0) return diff;
  return (a?.name || "").localeCompare(b?.name || "");
};

const TravelCustomFields = ({ formData, setFormData, customFieldsData }) => {
  const groups = [...getGroups(customFieldsData)].sort(byOrder);

  const handleFieldChange = (name, value) => {
    setFormData({ ...formData, customFields: { ...formData.customFields, [name]: value } });
  };

  if (!groups.length) {
    return <div className="col-span-12 text-sm font-medium text-gray-500">No custom fields have been configured.</div>;
  }

  const renderField = (field) => {
    const value = formData?.customFields?.[field.name] ?? "";
    const required = field.elective === "MANDATORY" || field.elective === "REQUIRED";
    const isList = field.validation?.["@type"] === "list";

    if (isList) {
      return (
        <TravelFormSelect
          key={field.uuid}
          label={getLabel(field)}
          value={value}
          required={required}
          options={(field.validation?.items || []).map((item) => ({ value: item.optValue, label: item.optLabel }))}
          onChange={(e) => handleFieldChange(field.name, e.target.value)}
        />
      );
    }

    switch (field.fieldType) {
      case "TEXTAREA":
        return (
          <div
            key={field.uuid}
            className="md:col-span-2">
            <label className="mb-1.5 block text-sm font-bold text-gray-700">
              {getLabel(field)} {required && <span className="text-red-500">*</span>}
            </label>
            <textarea
              value={value}
              required={required}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              rows={3}
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm transition-all duration-200 hover:border-gray-300 hover:shadow-md focus:border-[#2D0B31] focus:outline-none focus:ring-1 focus:ring-[#2D0B31]"
            />
          </div>
        );

      case "CHECKBOX":
        return (
          <div
            key={field.uuid}
            className="flex items-center gap-2">
            <input
              type="checkbox"
              id={field.uuid}
              checked={!!value}
              onChange={(e) => handleFieldChange(field.name, e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-[#2D0B31] focus:ring-[#2D0B31]"
            />
            <label
              htmlFor={field.uuid}
              className="cursor-pointer text-sm font-bold text-gray-700">
              {getLabel(field)} {required && <span className="text-red-500">*</span>}
            </label>
          </div>
        );

      case "DATE":
        return (
          <TravelFormInput
            key={field.uuid}
            type="date"
            label={getLabel(field)}
            value={value}
            required={required}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
          />
        );

      case "PHONE":
        return (
          <TravelFormInput
            key={field.uuid}
            type="tel"
            label={getLabel(field)}
            value={value}
            required={required}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
          />
        );

      default:
        return (
          <TravelFormInput
            key={field.uuid}
            label={getLabel(field)}
            value={value}
            required={required}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
          />
        );
    }
  };

  return (
    <div className="col-span-12 space-y-10">
      {groups.map((group) => (
        <div key={group.uuid}>
          <h4 className="mb-4 border-b border-gray-100 pb-2 text-lg font-bold text-[#2D0B31]">{getLabel(group)}</h4>
          <div className="grid grid-cols-1 gap-6 rounded-xl border border-gray-200 bg-gray-50/50 p-5 md:grid-cols-2">{[...(group.fields || [])].sort(byOrder).map(renderField)}</div>
        </div>
      ))}
    </div>
  );
};

export default TravelCustomFields;
